import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Instagram, Twitter, Music2, Facebook, AtSign, Cloud } from "lucide-react";
import { ShareButton } from "./ShareButton";
import { api, resolveAsset } from "./../lib/api";

// Lazy-load each platform's embed script once per page
const scriptUrls = {
  instagram: "https://www.instagram.com/embed.js",
  x: "https://platform.twitter.com/widgets.js",
  tiktok: "https://www.tiktok.com/embed.js",
  facebook: null, // no script — use iframe plugin
  threads: null,
  bluesky: null, // bsky uses iframe
};

const loaded = new Set();

function loadScript(platform) {
  const url = scriptUrls[platform];
  if (!url || loaded.has(url)) return;
  loaded.add(url);
  const s = document.createElement("script");
  s.src = url;
  s.async = true;
  document.body.appendChild(s);
}

const platformMeta = {
  instagram: { Icon: Instagram, label: "Instagram", color: "#E1306C" },
  x: { Icon: Twitter, label: "X", color: "#0A0F17" },
  tiktok: { Icon: Music2, label: "TikTok", color: "#0A0F17" },
  facebook: { Icon: Facebook, label: "Facebook", color: "#1877F2" },
  threads: { Icon: AtSign, label: "Threads", color: "#0A0F17" },
  bluesky: { Icon: Cloud, label: "Bluesky", color: "#0085FF" },
};

const Header = ({ platform, url }) => {
  const meta = platformMeta[platform] || platformMeta.x;
  return (
    <div className="flex items-center justify-between gap-3 border-b border-black/10 px-4 py-3">
      <span
        className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-wider"
        style={{ color: meta.color }}
      >
        <meta.Icon size={14} strokeWidth={2.4} />
        {meta.label}
      </span>
      <div className="flex items-center gap-2">
        <ShareButton
          url={url}
          title={`A #PUSB post shared via LAST — ${meta.label}`}
          label="Share"
        />
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid={`social-open-original-${platform}`}
          className="inline-flex items-center gap-1 rounded-sm border border-black/15 px-2 py-1 label-kicker text-[var(--brand-body)] transition-colors hover:border-[#0A0F17] hover:text-[var(--brand-ink)]"
        >
          Open on {meta.label} <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
};

export const SocialEmbed = ({ platform, url, caption, previewImage }) => {
  const containerRef = useRef(null);
  // Server-fetched OG preview, used by Facebook (Meta deprecated the
  // 3rd-party iframe plugin in late 2025 — pages with that iframe now
  // get "facebook.com refused to connect" on every device). Skipped when
  // an admin has already provided a manual `previewImage`.
  const [ogPreview, setOgPreview] = useState(null);
  // TikTok short URLs (vm.tiktok.com/<code>/, tiktok.com/t/<code>/...)
  // don't carry the numeric video_id that TikTok's embed.js requires,
  // so the blockquote stays as a fallback link. We resolve those URLs
  // via the backend's /social/tiktok-oembed endpoint and store the
  // resolved video_id here.
  const [resolvedTiktokId, setResolvedTiktokId] = useState(null);
  useEffect(() => {
    if (platform !== "facebook" || !url || previewImage) return undefined;
    let alive = true;
    api
      .get(`/og-preview?url=${encodeURIComponent(url)}`)
      .then((r) => { if (alive) setOgPreview(r.data || null); })
      .catch(() => { if (alive) setOgPreview(null); });
    return () => { alive = false; };
  }, [platform, url, previewImage]);

  // Compute the TikTok video_id we can extract locally. If we can't,
  // fall back to the oEmbed resolver.
  const localTiktokId = (() => {
    if (platform !== "tiktok" || !url) return "";
    const m = url.match(/\/video\/(\d{6,})/);
    return m ? m[1] : "";
  })();
  useEffect(() => {
    if (platform !== "tiktok" || !url) return undefined;
    if (localTiktokId) {
      setResolvedTiktokId(null);
      return undefined;
    }
    let alive = true;
    api
      .get(`/social/tiktok-oembed?url=${encodeURIComponent(url)}`)
      .then((r) => { if (alive && r.data?.video_id) setResolvedTiktokId(r.data.video_id); })
      .catch(() => { if (alive) setResolvedTiktokId(null); });
    return () => { alive = false; };
  }, [platform, url, localTiktokId]);

  useEffect(() => {
    loadScript(platform);
    // Re-trigger Instagram processing when its script is loaded
    if (platform === "instagram" && window.instgrm) {
      window.instgrm.Embeds.process();
    }
    if (platform === "x" && window.twttr?.widgets) {
      window.twttr.widgets.load(containerRef.current);
    }
    if (platform === "tiktok") {
      // TikTok's embed.js attaches a MutationObserver on first load, but
      // when an admin pastes a new TikTok URL after the first render the
      // observer can miss the late-mounted node. Re-injecting the script
      // forces a fresh scan of the DOM.
      const existing = document.querySelector('script[src*="tiktok.com/embed.js"]');
      if (existing) {
        existing.remove();
        loaded.delete("https://www.tiktok.com/embed.js");
        loadScript("tiktok");
      }
    }
  }, [platform, url, resolvedTiktokId]);

  return (
    <div
      data-testid={`social-embed-${platform}`}
      className="overflow-hidden rounded-md border border-black/10 bg-white flex flex-col h-full"
    >
      <Header platform={platform} url={url} />
      <div
        ref={containerRef}
        className="bg-white p-3 flex-1 overflow-y-auto"
        style={{ minHeight: 160 }}
      >
        {platform === "instagram" ? (
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={url}
            data-instgrm-version="14"
            style={{
              background: "#FFF",
              border: 0,
              margin: "1px auto",
              padding: 0,
              maxWidth: "540px",
              minWidth: "240px",
              width: "calc(100% - 2px)",
            }}
          >
            <a href={url}>View on Instagram</a>
          </blockquote>
        ) : platform === "x" ? (
          <blockquote className="twitter-tweet" data-dnt="true">
            <a href={url}>View on X</a>
          </blockquote>
        ) : platform === "tiktok" ? (
          // TikTok's embed.js requires the numeric video ID on the
          // blockquote — without it the script leaves the fallback link
          // in place. We try the canonical /video/<id>/ URL first; if
          // the admin pasted a short URL (vm.tiktok.com/<code>/) the
          // oEmbed resolver fetches the numeric id server-side.
          (() => {
            const videoId = localTiktokId || resolvedTiktokId || "";
            if (!videoId) {
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-tiktok-fallback"
                  className="block px-2 py-6 text-center text-sm text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
                >
                  Resolving TikTok preview… open this post on TikTok
                </a>
              );
            }
            return (
              <blockquote
                key={videoId}
                className="tiktok-embed"
                cite={url}
                data-video-id={videoId}
                style={{ maxWidth: "605px", minWidth: "325px" }}
              >
                <a href={url}>View on TikTok</a>
              </blockquote>
            );
          })()
        ) : platform === "bluesky" ? (
          <iframe
            src={`https://embed.bsky.app/embed?url=${encodeURIComponent(url)}`}
            style={{ width: "100%", minHeight: 320, border: 0 }}
            title="Bluesky post"
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        ) : platform === "facebook" ? (
          // Meta blocks 3rd-party iframe embeds without an app token. Use
          // a manually-supplied preview image if the admin uploaded one,
          // otherwise fall back to a server-fetched OG card or a clean
          // "Open on Facebook" link card.
          previewImage || ogPreview?.image ? (
            <a
              href={(ogPreview && ogPreview.url) || url}
              target="_blank"
              rel="noreferrer"
              data-testid="social-fb-card"
              className="group flex flex-col overflow-hidden"
            >
              <div className="aspect-[16/9] w-full overflow-hidden bg-[#F8FAFC]">
                <img
                  src={resolveAsset(previewImage || ogPreview.image)}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </div>
              <div className="px-4 py-3">
                <h4 className="line-clamp-2 font-display text-base font-bold leading-tight text-[var(--brand-ink)] group-hover:text-[#1877F2]">
                  {ogPreview?.title || caption || "Facebook post"}
                </h4>
                {ogPreview?.description ? (
                  <p className="mt-1 line-clamp-3 text-sm leading-relaxed text-[var(--brand-body)]">
                    {ogPreview.description}
                  </p>
                ) : null}
                <span className="mt-2 inline-flex items-center gap-1 label-kicker text-[#1877F2]">
                  Open on Facebook <ExternalLink size={11} />
                </span>
              </div>
            </a>
          ) : (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              data-testid="social-fb-fallback"
              className="flex items-center justify-between gap-3 px-4 py-5 text-sm text-[var(--brand-ink)] hover:bg-[#1877F2]/5"
            >
              <span className="flex items-center gap-2">
                <Facebook size={18} className="text-[#1877F2]" />
                <span className="font-display font-bold">Open this post on Facebook</span>
              </span>
              <ExternalLink size={14} className="text-[var(--brand-body)]" />
            </a>
          )
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block px-2 py-6 text-center text-sm text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
          >
            Open this post on {platformMeta[platform]?.label || platform}
          </a>
        )}
      </div>
      {caption ? (
        <p className="border-t border-black/10 bg-[#F8FAFC] px-4 py-3 text-sm leading-relaxed text-[var(--brand-body)]">
          {caption}
        </p>
      ) : null}
    </div>
  );
};
